<template>
  <div
    id="events-view"
    class="pa-4"
  >
    <view-page-header
      all-teams
      :show-team-select="false"
    />

    <portal
      key="eventsPortal"
      to="headerFilters"
    >
      <v-row
        align="center"
      >
        <v-col cols="auto">
          <v-btn
            v-for="eventType in mainEventTypesList"
            :key="'eventTypeButton'+eventType.key"
            :text="filters.eventType !== eventType.key"
            :dark="filters.eventType === eventType.key"
            class="text-none"
            :color="filters.eventType === eventType.key ? eventType.color : undefined"
            @click="filters.eventType === eventType.key ? filters.eventType = undefined : filters.eventType = eventType.key"
          >
            {{ eventType.text }}
          </v-btn>
          <v-menu bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                class="text-none"
                :color="currentSelectedEventType && currentSelectedEventType.is_other ? currentSelectedEventType.color : undefined"
                :text="!currentSelectedEventType || !currentSelectedEventType.is_other"
                :dark="currentSelectedEventType && currentSelectedEventType.is_other"
                v-on="on"
              >
                {{ currentSelectedEventType && currentSelectedEventType.is_other ? currentSelectedEventType.text : $t('event_type.other') }}
                <v-icon
                  right
                  x-small
                >
                  far fa-chevron-down
                </v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-list dense>
                <v-list-item
                  v-if="currentSelectedEventType && currentSelectedEventType.is_other"
                  @click="filters.eventType = undefined"
                >
                  <v-list-item-title>
                    {{ $t('remove_filter.title') }}
                  </v-list-item-title>
                  <v-list-item-action>
                    <v-icon small>
                      far fa-times
                    </v-icon>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="currentSelectedEventType && currentSelectedEventType.is_other" />
                <v-list-item
                  v-for="eventType in otherEventTypesList"
                  :key="'otherEventType'+eventType.key"
                  @click="filters.eventType = eventType.key"
                >
                  <v-list-item-title>
                    {{ eventType.text }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </v-col>
        <v-col cols="auto">
          <v-autocomplete
            v-model="filters.coachId"
            :items="coaches.data"
            item-value="id"
            item-text="last_name"
            :filter="(item, queryText) => {
              return (item.first_name + item.last_name).toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
            }"
            :loading="coaches.loading"
            solo
            :chips="false"
            clearable
            hide-details
            :placeholder="$t('text_input.search_by_coach.title')"
            style="width: 250px"
          >
            <template v-slot:selection="{ item }">
              <span>{{ item.first_name }} {{ item.last_name }}</span>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.last_name }}, {{ item.first_name }}
                </v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col
          v-if="!hasNoPlayers"
          cols="auto"
        >
          <players-autocomplete
            key="eventPlayersFilter"
            v-model="filters.playerId"
            :players="playersByTeam"
            :loading="players.loading"
            solo
            :chips="false"
            clearable
            hide-details
            :placeholder="$t('text_input.search_by_player.title')"
            style="width: 250px"
          />
        </v-col>
        <v-col
          v-if="!hasNoPlayers"
          cols="auto"
        >
          <v-select
            key="eventTeamFilter"
            v-model="filters.teamId"
            :items="selectTeamItems"
            item-value="id"
            item-text="short_name"
            :label="$t('table_headers.admin_staff.teams')"
            background-color="white"
            solo
            hide-details
            :menu-props="{ maxWidth: '250px' }"
            style="width: 250px"
          >
            <template v-slot:selection="{item}">
              <v-avatar size="30">
                <img
                  :src="user.organisation_settings.logo_url"
                >
              </v-avatar>
              <span
                class="ml-2 font-weight-bold"
                style="max-width: 160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
              >{{ item.short_name }}</span>
            </template>
            <template v-slot:item="{item}">
              <v-avatar size="30">
                <img
                  :src="user.organisation_settings.logo_url"
                >
              </v-avatar>
              <span
                class="ml-2"
                style="max-width: 160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
              >{{ item.short_name }}</span>
            </template>
          </v-select>
        </v-col>
      </v-row>
    </portal>

    <section>
      <v-row no-gutters>
        <v-col>
          <v-card
            class="rounded-lg overflow-hidden"
            elevation="10"
          >
            <div style="height: 2px">
              <v-progress-linear
                v-show="events.loading"
                indeterminate
                height="2px"
              />
            </div>
            <div class="pa-3">
              <v-row
                no-gutters
                class="mb-2"
                align="center"
              >
                <v-col cols="auto">
                  <v-btn
                    icon
                    @click="$refs.calendar.prev()"
                  >
                    <v-icon
                      small
                      v-text="$vuetify.icons.values.prev"
                    />
                  </v-btn>
                </v-col>
                <v-col
                  v-if="$refs.calendar"
                  cols="auto"
                  class="px-4 text-subtitle-1"
                >
                  {{ $dayjs($refs.calendar.lastStart.date).format('ll') }} -
                  {{ $dayjs($refs.calendar.lastEnd.date).format('ll') }}
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    icon
                    @click="$refs.calendar.next()"
                  >
                    <v-icon
                      small
                      v-text="$vuetify.icons.values.next"
                    />
                  </v-btn>
                </v-col>
                <v-col class="ml-3">
                  <v-btn
                    text
                    color="primary"
                    class="text-none"
                    @click="today = $dayjs().format('YYYY-MM-DD')"
                  >
                    {{ $t("button.today") }}
                  </v-btn>
                </v-col>
                <v-spacer />
                <v-col cols="auto">
                  <v-select
                    v-model="calendarView"
                    solo
                    flat
                    hide-details
                    filled
                    dense
                    style="width: 150px"
                    :items="[ { value: 'week', text: $t('events.calendar.option.weekly') }, { value: 'month', text: $t('events.calendar.option.monthly') }]"
                  />
                </v-col>
              </v-row>
              <v-sheet :height="user.organisation_settings.has_timezone_management ? '950px' : undefined">
                <v-calendar
                  ref="calendar"
                  v-model="today"
                  :locale="$i18n.locale"
                  :events="events.data"
                  :first-interval="user.organisation_settings.has_timezone_management ? 0 : 6"
                  :interval-count="user.organisation_settings.has_timezone_management ? 24 : 17"
                  :weekdays="calendarWeekdays"
                  :type="calendarView"
                  @change="fetchEvents"
                  @mousedown:event="startDrag"
                  @mousedown:time="startTime"
                  @mousemove:time="mouseMove"
                  @mouseup:time="endDrag"
                  @click:event="calendarView === 'month' ? showEvent($event) : undefined"
                  @mouseleave.native="cancelDrag"
                >
                  <template v-slot:event="{ event, timed }">
                    <div
                      class="v-event-draggable"
                    >
                      <div>
                        <strong>{{ event.name }}</strong>, {{ $dayjs(event.start).format(getDateTimeFormatForUser('HH:mm')) }} -
                        {{ $dayjs(event.end).format(getDateTimeFormatForUser('HH:mm')) }}
                      </div>
                      <div
                        v-for="team in event.teams"
                        :key="event.id + '_' + team.id"
                        class="opacity--08"
                      >
                        &middot; {{ team.name }}
                      </div>
                    </div>
                    <div
                      v-if="timed"
                      class="v-event-drag-bottom"
                      @mousedown.stop="extendBottom(event)"
                    />
                  </template>
                </v-calendar>
              </v-sheet>
              <div
                v-if="user.organisation_settings.has_timezone_management"
                class="caption mt-3"
              >
                {{ timezones.find(tz => tz.tzCode === timezone) ? timezones.find(tz => tz.tzCode === timezone).name : '' }}
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <event-sidebar
      v-if="currentEvent.drawer"
      :event-view="true"
      :event-details.sync="currentEvent.event"
      :value.sync="currentEvent.drawer"
      :loading="currentEvent.loading"
      @update="showEvent({ event: currentEvent.event }, true)"
    />

    <v-navigation-drawer
      right
      clipped
      app
      stateless
      :value="calendarSidebar"
      :width="570"
    >
      <calendar-sidebar
        full-height
      />
    </v-navigation-drawer>

    <card-dialog
      v-model="moveEventConfirmationDialog.visible"
      max-width="500px"
      :title="$t('events.reschedule_dialog.title')"
      :persistent="false"
      @input="!$event ? cancelEventUpdate() : undefined"
    >
      <v-card-text
        v-if="moveEventConfirmationDialog.newStart && moveEventConfirmationDialog.newEnd"
        class="pa-5"
      >
        {{ $t("events.reschedule_dialog.message") }} <strong>
          {{
            moveEventConfirmationDialog.newStart.format(getDateTimeFormatForUser(getDateTimeFormatForUser('ll, HH:mm')))
          }} -
          {{ moveEventConfirmationDialog.newEnd.format(getDateTimeFormatForUser(getDateTimeFormatForUser('HH:mm'))) }}
        </strong>?
      </v-card-text>

      <v-divider />

      <template v-slot:actions>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            color="grey"
            @click="cancelEventUpdate()"
          >
            {{ $t('button.no_cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="moveEventConfirmationDialog.submitting"
            @click="startEventUpdate()"
          >
            <v-icon
              small
              left
            >
              fa fa-check
            </v-icon>
            {{ $t("button.yes_confirm") }}
          </v-btn>
        </v-card-actions>
      </template>
    </card-dialog>

    <v-btn
      fab
      color="primary"
      :style="{position:'fixed', bottom: '20px', right: calendarSidebar || currentEvent.drawer ? '590px' : '20px', 'z-index': '10', transition: 'right 0.25s ease'}"
      @click="calendarSidebar = !calendarSidebar; currentEvent.drawer = false"
    >
      <v-icon>fal fa-calendar-week</v-icon>
    </v-btn>
  </div>
</template>

<script>
import ViewPageHeader from '@/components/ViewPageHeader'
import { CoachApiService, EventApiService, PlayerApiService } from '@/data/services'
import dayjs from 'dayjs'
import { EventType, EventTypes } from '@/data/models'
import { EventBus } from '@/plugins/bus'
import CardDialog from '@/components/helpers/CardDialog'
import { cloneDeep } from 'lodash'
import PlayersAutocomplete from '@/components/helpers/PlayersAutocomplete'
import timezones from 'timezones-list'
import CalendarSidebar from '@/components/calendar/CalendarSidebar.vue'
import i18n from '@/plugins/i18n'
import EventSidebar from '@/components/events/EventSidebar.vue'
import { getDateTimeFormatForUser } from '@/utils/helpers'

export default {
  name: 'EventsView',
  components: {
    EventSidebar,
    CalendarSidebar,
    PlayersAutocomplete,
    CardDialog,
    ViewPageHeader
  },
  data: () => ({
    calendarView: 'week',
    events: {
      data: [],
      loading: false
    },
    timezone: null,
    currentEvent: {
      loading: false,
      drawer: false,
      event: undefined,
      tab: 'players'
    },
    fileUpload: {
      error: undefined,
      filename: undefined,
      progress: 0,
      submitting: false,
      file: undefined
    },
    statsportFileUpload: {
      error: undefined,
      filename: undefined,
      progress: 0,
      submitting: false,
      file: undefined
    },
    fitogetherFileUpload: {
      error: undefined,
      filename: undefined,
      progress: 0,
      submitting: false,
      file: undefined
    },
    playertekFileUpload: {
      error: undefined,
      filename: undefined,
      progress: 0,
      submitting: false,
      file: undefined
    },
    catapultOneFileUpload: {
      error: undefined,
      filename: undefined,
      progress: 0,
      submitting: false,
      file: undefined
    },
    periodEditor: {
      visible: false,
      players: [],
      periods: [],
      min: 0,
      max: 100,
      devicesIds: [],
      periodsSaved: false,
      includeGps: true
    },
    devices: {
      loading: false,
      data: [],
      allowedDeviceIds: []
    },
    today: dayjs().format('YYYY-MM-DD'),
    submitting: false,
    dragEvent: null,
    dragStartMouseOffset: null,
    dragEventOriginalStart: null,
    filters: {
      teamId: 'all',
      playerId: null,
      coachId: null,
      eventType: undefined
    },
    players: {
      data: [],
      loading: false
    },
    coaches: {
      data: [],
      loading: false
    },
    addEventFeedback: {
      dialog: false,
      player: null
    },
    moveEventConfirmationDialog: {
      visible: false,
      event: null,
      newStart: null,
      newEnd: null,
      submitting: false
    },
    calendarSidebar: false,
    matchPrepareModal: false
  }),
  computed: {
    dayjs() {
      return dayjs
    },
    currentTeamId() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]?.id
    },
    ranges: {
      set: function (value) {
        this.periodEditor.range = value
      },
      get: function () {
        return this.periodEditor.range
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    integrations() {
      return this.user.organisation_settings.integrations || []
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    playersByTeam() {
      if (!this.filters.teamId || this.filters.teamId === 'all') return this.players.data

      return this.players.data.filter(p => p.team_id === this.filters.teamId)
    },
    selectTeamItems() {
      const selectTeamItems = []

      selectTeamItems.push({
        id: 'all',
        short_name: i18n.t('dropdown.team_selection.all_teams')
      })
      selectTeamItems.push({ divider: true })
      selectTeamItems.push(...this.teams)

      return selectTeamItems
    },
    currentSelectedEventType() {
      if (!this.filters.eventType) return undefined

      const eventTypes = Object.keys(EventTypes).map(k => {
        return {
          ...EventTypes[k],
          key: k
        }
      })

      return eventTypes.find(t => t.key === this.filters.eventType)
    },
    hasNoPlayers() {
      return this.user.organisation_settings?.has_no_players || false
    },
    calendarWeekdays() {
      if (this.user.organisation_settings?.unit_system === 'imperial') {
        return [0, 1, 2, 3, 4, 5, 6]
      } else {
        return [1, 2, 3, 4, 5, 6, 0]
      }
    }
  },
  watch: {
    'filters.teamId': function () {
      const oldPlayerId = this.filters.playerId
      this.filters.playerId = null
      if (oldPlayerId === this.filters.playerId) this.fetchEvents()
    },
    'filters.playerId': function () {
      this.fetchEvents()
    },
    'filters.coachId': function () {
      this.fetchEvents()
    },
    'filters.eventType': function () {
      this.fetchEvents()
    },
    'periodEditor.includeGps': function (val, oldVal) {
      if (val) {
        this.devices.allowedDeviceIds = cloneDeep(this.periodEditor.devicesIds)
        this.periodEditor.devicesIds = []
      } else {
        this.periodEditor.devicesIds = cloneDeep(this.devices.allowedDeviceIds)
        this.devices.allowedDeviceIds = []
      }
    }
  },
  created: function () {
    this.timezones = timezones
    this.timezone = dayjs.tz.guess()

    this.eventTypes = EventTypes
    this.eventTypeMatch = EventType.MATCH

    this.mainEventTypesList = Object.keys(EventTypes).map(k => {
      return {
        ...EventTypes[k],
        key: k
      }
    }).filter(e => e.is_other === false)
    this.otherEventTypesList = Object.keys(EventTypes).map(k => {
      return {
        ...EventTypes[k],
        key: k
      }
    }).filter(e => e.is_other === true)

    this.fetchAllPlayers()
    this.fetchAllCoaches()
  },
  mounted() {
    EventBus.$on(this.$events.EVENT_CREATED, (event, shouldFetch = true) => {
      if (shouldFetch) this.fetchEvents()
    })

    EventBus.$on(this.$events.EVENT_UPDATED, (event) => {
      if (this.currentEvent.event && this.currentEvent.event.id === event.id) {
        this.showEvent({ event }, true)
      }
      this.fetchEvents()
    })

    EventBus.$on(this.$events.EVENT_DELETED, (event) => {
      this.fetchEvents()
      this.currentEvent.drawer = false
      this.currentEvent.event = undefined
      this.currentEvent.tab = 'players'
    })

    EventBus.$on(this.$events.TRAINING_PROGRAM_INSTANCE_UPDATED, (trainingProgramInstance) => {
      if (this.currentEvent.event && this.currentEvent.event.training_program_instance_id === trainingProgramInstance.id) {
        this.showEvent({ event: this.currentEvent.event }, true)
      }
      this.fetchEvents()
    })

    this.$refs.calendar.scrollToTime('07:00')
  },
  beforeDestroy() {
    EventBus.$off(this.$events.EVENT_CREATED)
    EventBus.$off(this.$events.EVENT_UPDATED)
    EventBus.$off(this.$events.EVENT_DELETED)
    EventBus.$off(this.$events.TRAINING_PROGRAM_INSTANCE_UPDATED)
  },
  methods: {
    getDateTimeFormatForUser,
    async fetchAllPlayers() {
      try {
        this.players.loading = true

        const result = await PlayerApiService.getSimpleList({
          order_by: 'last_name',
          order: 'ASC'
        })

        this.players.data = result.data
      } finally {
        this.players.loading = false
      }
    },
    async fetchAllCoaches() {
      try {
        this.coaches.loading = true

        const result = await CoachApiService.getPaginated({
          order_by: 'last_name',
          order: 'ASC'
        })

        this.coaches.data = result.data
      } finally {
        this.coaches.loading = false
      }
    },
    async fetchEvents(dates = null) {
      this.events.loading = true

      const from = dates ? dates.start.date : this.$refs.calendar.lastStart.date
      const to = dates ? dates.end.date : this.$refs.calendar.lastEnd.date

      try {
        const response = await EventApiService.getAll({
          from: dayjs(from).format('YYYY-MM-DD'),
          to: dayjs(to).format('YYYY-MM-DD'),
          team_id: this.filters.teamId !== 'all' ? this.filters.teamId : undefined,
          player_id: this.filters.playerId || undefined,
          coach_id: this.filters.coachId || undefined,
          event_type: this.filters.eventType || undefined,
          compact: true
        })

        this.events.data = response.data.map(event => {
          return {
            id: event.id,
            name: event.title,
            start: dayjs(event.start_at).toDate(),
            end: dayjs(event.end_at).toDate(),
            start_at: event.start_at,
            end_at: event.end_at,
            color: this.eventTypes[event.event_type].color,
            timed: true,
            type: event.event_type,
            teams: event.teams,
            editing_rules: event.editing_rules,
            feedback_requests_count: event.feedback_requests_count
          }
        })
      } catch (e) {

      } finally {
        this.events.loading = false
      }
    },
    async showEvent({ event }, forceRefresh = false) {
      if (!this.$can('view-event')) return

      if (!forceRefresh && this.currentEvent.event && this.currentEvent.event.id === event.id) {
        this.currentEvent.drawer = true
        this.calendarSidebar = false
        return
      }

      this.currentEvent.loading = true
      this.currentEvent.drawer = true
      this.calendarSidebar = false

      try {
        const response = await EventApiService.getEventDetails(event.id, { compact_players: 1 })

        this.currentEvent.event = response.data

        this.loadEventPeriods(response.data.periods)
      } catch (e) {

      } finally {
        this.currentEvent.loading = false
      }
    },
    loadEventPeriods(periods) {
      this.periodEditor.periods = periods.map(period => {
        return {
          id: period.id,
          title: period.title,
          range: [this.$dayjs(period.start_at).diff(this.$dayjs(this.currentEvent.event.start_at), 'seconds'), this.$dayjs(period.end_at).diff(this.$dayjs(this.currentEvent.event.start_at), 'seconds')],
          is_play_time: period.is_play_time
        }
      })

      if (this.periodEditor.periods.length > 0) this.periodEditor.periodsSaved = true
    },
    startDrag({
                event,
                timed
              }) {
      if ((this.calendarView === 'month' && event) || (event && timed)) {
        this.dragEvent = event
        this.dragStartMouseOffset = null
      }
    },
    toTime(tms) {
      return new Date(tms.year, tms.month - 1, tms.day, tms.hour, tms.minute).getTime()
    },
    roundTime(time, down = true) {
      const roundTo = 15 // minutes
      const roundDownTime = roundTo * 60 * 1000

      return down
        ? time - time % roundDownTime
        : time + (roundDownTime - (time % roundDownTime))
    },
    startTime(tms) {
      const mouse = this.toTime(tms)

      if (this.dragEvent && this.dragStartMouseOffset === null) {
        const start = this.dragEvent.start

        this.dragStartMouseOffset = mouse - start
      } else {
        if (!this.$can('create-event')) return

        EventBus.$emit(this.$events.OPEN_DIALOG_EVENT_FORM, { startAt: dayjs(tms.date).hour(tms.hour).minute((tms.minute / 15).toFixed(0) * 15).second(0) })
      }
    },
    extendBottom(event) {
      this.dragEvent = event
      this.dragEventOriginalStart = event.start
    },
    mouseMove(tms) {
      if (this.dragEvent && (
        (this.dragEvent.editing_rules && (!this.dragEvent.editing_rules.can_edit_time || !this.dragEvent.editing_rules.can_edit_or_delete)) ||
        this.dragEvent.feedback_requests_count > 0 || !this.$can('edit-event')
      )) return

      const mouse = this.toTime(tms)

      if (this.dragEvent && this.dragStartMouseOffset !== null) {
        const start = this.dragEvent.start
        const end = this.dragEvent.end
        const duration = end - start
        const newStartTime = mouse - this.dragStartMouseOffset
        const newStart = this.roundTime(newStartTime)
        const newEnd = newStart + duration

        this.dragEvent.start = newStart
        this.dragEvent.end = newEnd
      } else if (this.dragEvent && this.dragEventOriginalStart !== null) {
        const mouseRounded = this.roundTime(mouse, false)

        const min = Math.min(mouseRounded, this.dragEventOriginalStart)
        const max = Math.max(mouseRounded, this.dragEventOriginalStart)

        this.dragEvent.start = min
        this.dragEvent.end = max
      }
    },
    async endDrag() {
      if (!this.dragEvent) return

      const dragEvent = this.dragEvent

      this.dragStartMouseOffset = null
      this.dragEvent = null
      this.dragEventOriginalStart = null

      const start = dayjs(dragEvent.start)
      const originalStart = dayjs(dragEvent.start_at)
      const end = dayjs(dragEvent.end)
      const originalEnd = dayjs(dragEvent.end_at)

      if (!start.isSame(originalStart, 'second') || !end.isSame(originalEnd, 'second')) {
        this.moveEventConfirmationDialog.event = dragEvent
        this.moveEventConfirmationDialog.newStart = start
        this.moveEventConfirmationDialog.newEnd = end
        this.moveEventConfirmationDialog.visible = true
      }

      this.showEvent({ event: dragEvent })
    },
    cancelDrag() {
      this.dragStartMouseOffset = null
      this.dragEvent = null
      this.dragEventOriginalStart = null
    },
    cancelEventUpdate() {
      this.fetchEvents()

      this.moveEventConfirmationDialog.visible = false
    },
    async startEventUpdate() {
      try {
        this.moveEventConfirmationDialog.submitting = true

        const newEvent = await this.updateEvent(this.moveEventConfirmationDialog.event, this.moveEventConfirmationDialog.newStart, this.moveEventConfirmationDialog.newEnd)

        this.showEvent({ event: newEvent }, true)
      } catch (e) {

      } finally {
        this.moveEventConfirmationDialog.visible = false
        this.moveEventConfirmationDialog.submitting = false
      }
    },
    async updateEvent(event, newStart, newEnd) {
      try {
        const result = await EventApiService.update({
          id: event.id,
          start_at: dayjs(newStart).toISOString(),
          end_at: dayjs(newEnd).toISOString()
        })

        EventBus.$emit(this.$events.EVENT_UPDATED, result.data, false)

        return result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.something_went_wrong_event.error'),
          color: 'error'
        })
        throw e
      }
    }
  }
}
</script>
